import React, { useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog.jsx";
import UsersModal from "./UsersModal.jsx";
import LockChip from "./LockChip.jsx";
import useHRBootstrap from "./useHRBootstrap.js";
import useMonthLock from "./useMonthLock.js";
import "./hr-app.css";
import "./hr-extras.css";

/**
 * Main HR application shell.
 *
 * Renders the original (HTML-injected) HR UI plus a few React-managed overlays:
 *   - ConfirmDialog (custom confirm replacement, exposed as window.hhConfirm)
 *   - UsersModal (admin-only user management)
 *   - LockChip (admin-only payroll month lock/unlock control)
 *   - Loading / error states
 *
 * Bootstrap (state fetch + script injection + header chip + role-aware tabs)
 * lives in useHRBootstrap. Month-lock helpers live in useMonthLock.
 */
export default function HRApp({ user, onLogout }) {
  const mountRef = useRef(null);
  const [usersOpen, setUsersOpen] = useState(false);

  const { loading, error, employees, isAdmin } = useHRBootstrap({
    user,
    mountRef,
    onLogout,
    onUsersClick: () => setUsersOpen(true),
  });

  const lockState = useMonthLock(user);

  return (
    <>
      <ConfirmDialog />

      {usersOpen && isAdmin ? (
        <UsersModal
          onClose={() => setUsersOpen(false)}
          employees={employees}
          currentUserId={user?.id}
        />
      ) : null}

      {!loading && isAdmin ? (
        <LockChip
          monthKey={lockState.monthKey}
          monthDisplay={lockState.monthDisplay}
          locked={lockState.locked}
          tab={lockState.tab}
          onLock={lockState.lockMonth}
          onUnlock={lockState.unlockMonth}
        />
      ) : null}

      {loading ? (
        <div className="hh-fullscreen-loader" data-testid="hr-loading">
          <div className="hh-spinner"></div>
          <div>Loading HR data…</div>
        </div>
      ) : null}

      {error ? (
        <div className="hh-fullscreen-loader" data-testid="hr-error">
          <div style={{ color: "#c0392b" }}>Error: {error}</div>
        </div>
      ) : null}

      <div ref={mountRef} id="hr-mount" data-testid="hr-mount" />
    </>
  );
}
